import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  ColorPicker,
  Divider,
  Drawer,
  Dropdown,
  Empty,
  Checkbox,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Image,
  List,
  Menu,
  Modal,
  Segmented,
  Select,
  Slider,
  Space,
  Spin,
  Switch,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import type { MenuProps } from 'antd';
import type { UploadRequestOption as RcUploadRequestOption } from 'rc-upload/lib/interface';
import type { Dayjs } from 'dayjs';
import {
  ArrowLeftOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
  SettingOutlined,
  CalendarOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UploadOutlined,
  SearchOutlined,
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  LoadingOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  createEmptyLandingDocument,
  createLandingPage,
  getLandingPage,
  updateLandingPage,
  type LandingBlock,
  type LandingDocument,
  type LandingFormBlock,
  type LandingFormField,
  type LandingImageBlock,
  type LandingBackgroundBlock,
  type LandingVideoBlock,
  type LandingPageDTO,
  type LandingPagePayload,
  type LandingTextBlock,
  type LandingFooterSettings,
  type ImageSearchResult,
  LANDING_DEFAULT_FONT_FAMILY,
  uploadLandingBlockAsset,
  uploadLandingBackground,
  searchImages,
  checkLandingSlugAvailability,
} from '../../services/landingPages';
import { listCustomers, type CustomerDTO } from '../../services/customers';
import { LANDING_PUBLIC_BASE_URL, buildLandingPublicUrl } from '../../utils/landingUrls';
import './LandingBuilderPage.css';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea, Search } = Input;

type BlockType = 'heading' | 'paragraph' | 'image' | 'video' | 'form' | 'background';

type EditableLanding = {
  id?: number;
  name: string;
  slug: string;
  description: string;
  customerId?: number;
  customerName?: string;
  backgroundColor?: string | null;
  backgroundImageUrl?: string | null;
  backgroundOverlayColor?: string | null;
  backgroundOverlayOpacity?: number;
  primaryColor?: string | null;
  buttonLabel?: string | null;
  successMessage?: string | null;
  eventDate?: string | null;
  reminderDate?: string | null;
  reminderEnabled?: boolean | null;
  reminderEmailSubject?: string | null;
  reminderEmailBody?: string | null;
  couponEmailSubject?: string | null;
  couponEmailBody?: string | null;
  emailSenderName?: string | null;
  published: boolean;
  document: LandingDocument;
};

type ContextMenuState = {
  x: number;
  y: number;
  blockId?: string;
} | null;

type DragState = {
  id: string;
  offsetX: number;
  offsetY: number;
} | null;

type ResizeState = {
  id: string;
  originX: number;
  originY: number;
  startWidth: number;
  startHeight: number;
} | null;

const FOOTER_BLOCK_ID = '__footer__';

const CENTER_SNAP_TOLERANCE = 6;

type SlugCheckState = {
  status: 'idle' | 'loading' | 'available' | 'unavailable' | 'error';
  normalized?: string;
  message?: string;
};

const DEFAULT_FORM_FIELDS: LandingFormField[] = [
  {
    id: 'firstName',
    type: 'text',
    label: 'Nome',
    placeholder: 'Inserisci il nome',
    required: false,
    visible: true,
  },
  {
    id: 'lastName',
    type: 'text',
    label: 'Cognome',
    placeholder: 'Inserisci il cognome',
    visible: true,
  },
  {
    id: 'email',
    type: 'email',
    label: 'Email',
    placeholder: 'nome@email.com',
    required: true,
    visible: true,
  },
  {
    id: 'phoneNumber',
    type: 'phone',
    label: 'Telefono',
    placeholder: '+39 ...',
    visible: true,
  },
];

const FALLBACK_KEYWORDS: Record<string, string[]> = {
  mare: ['sea', 'beach', 'ocean', 'waves', 'coast'],
  spiaggia: ['beach', 'sea', 'sand', 'tropical'],
  montagna: ['mountain', 'alps', 'snow', 'nature'],
  citta: ['city', 'urban', 'skyline'],
  natura: ['nature', 'forest', 'green', 'outdoor'],
  ufficio: ['office', 'business', 'workspace', 'team'],
  lavoro: ['work', 'business', 'team', 'office'],
  tecnologia: ['technology', 'laptop', 'startup', 'software'],
  marketing: ['marketing', 'digital', 'strategy', 'growth'],
  viaggio: ['travel', 'vacation', 'adventure', 'tourism'],
  hotel: ['hotel', 'resort', 'hospitality'],
  ristorante: ['restaurant', 'food', 'dining'],
  cibo: ['food', 'gourmet', 'meal'],
  benessere: ['wellness', 'relax', 'spa', 'health'],
  salute: ['health', 'medical', 'wellness', 'doctor'],
  palestra: ['fitness', 'gym', 'training'],
  famiglia: ['family', 'people', 'home'],
  matrimonio: ['wedding', 'bride', 'groom', 'couple'],
  auto: ['car', 'vehicle', 'automotive'],
  immobili: ['real estate', 'house', 'architecture'],
  casa: ['home', 'interior', 'living'],
  sport: ['sport', 'athlete', 'competition'],
};

const normalizeSearchTerm = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const buildFallbackTags = (query: string): string[] => {
  const normalizedQuery = normalizeSearchTerm(query);
  const baseTokens = normalizedQuery.split(/\s+/).filter(Boolean);
  const originalTokens = query
    .toLowerCase()
    .split(/\s+/)
    .map((token) => token.trim())
    .filter(Boolean);
  const tags = new Set<string>();

  originalTokens.forEach((token) => {
    if (token) {
      tags.add(token);
    }
  });

  baseTokens.forEach((token) => {
    tags.add(token);
    const extras = FALLBACK_KEYWORDS[token];
    if (extras) {
      extras.forEach((extra) => tags.add(extra));
    }
  });

  if (tags.size === 0 && normalizedQuery) {
    tags.add(normalizedQuery);
  }

  return Array.from(tags);
};

const createImageFallbackResults = (query: string, count = 12): ImageSearchResult[] => {
  const tags = buildFallbackTags(query);
  if (tags.length === 0) {
    return [];
  }

  const sanitized = tags.map((tag) => tag.replace(/\s+/g, '+')).join(',');
  if (!sanitized) {
    return [];
  }

  const timestamp = Date.now();

  return Array.from({ length: count }, (_, index) => {
    const seed = `${sanitized}-${timestamp}-${index}`;
    return {
      id: `fallback-${seed}`,
      thumbnail: `https://loremflickr.com/480/360/${sanitized}?lock=${index + 1}&random=${timestamp}`,
      full: `https://loremflickr.com/1080/720/${sanitized}?lock=${index + 1}&random=${timestamp}`,
      title: `Suggerimento per "${query.trim()}"`,
      provider: 'LoremFlickr',
    };
  });
};
const BLOCK_LABEL: Record<BlockType, string> = {
  heading: 'Titolo',
  paragraph: 'Paragrafo',
  image: 'Immagine',
  video: 'Video',
  form: 'Form contatti',
  background: 'Sfondo colorato',
};

const FONT_OPTIONS = [
  { label: 'Predefinito (Ant Design)', value: LANDING_DEFAULT_FONT_FAMILY },
  { label: 'Usa font tema pagina', value: 'inherit' },
  { label: 'Inter', value: "'Inter', sans-serif" },
  { label: 'Poppins', value: "'Poppins', sans-serif" },
  { label: 'Montserrat', value: "'Montserrat', sans-serif" },
  { label: 'Space Grotesk', value: "'Space Grotesk', sans-serif" },
  { label: 'Lora', value: "'Lora', serif" },
  { label: 'Playfair Display', value: "'Playfair Display', serif" },
];

const DEFAULT_BLOCK_HEIGHT: Record<BlockType, number> = {
  heading: 80,
  paragraph: 120,
  image: 180,
  video: 200,
  form: 360,
  background: 220,
};

const isFontBlock = (
  block: LandingBlock,
): block is LandingTextBlock =>
  block.type === 'heading' || block.type === 'paragraph';

const createId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2);
};

const resolveFontFamily = (font?: string | null) =>
  font && font.trim().length > 0 ? font.trim() : LANDING_DEFAULT_FONT_FAMILY;

const clamp01 = (value?: number | null) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 0;
  }
  return Math.min(Math.max(value, 0), 1);
};

const resolveOverlayColor = (color: string | null | undefined, opacity: number) => {
  const clamped = clamp01(opacity);
  if (clamped <= 0) {
    return 'rgba(0, 0, 0, 0)';
  }
  if (!color) {
    return `rgba(15, 23, 42, ${clamped})`;
  }
  const value = color.trim();
  const hexMatch = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(value);
  if (hexMatch) {
    let hex = hexMatch[1];
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((char) => `${char}${char}`)
        .join('');
    }
    const int = parseInt(hex, 16);
    const r = (int >> 16) & 255;
    const g = (int >> 8) & 255;
    const b = int & 255;
    return `rgba(${r}, ${g}, ${b}, ${clamped})`;
  }
  if (value.startsWith('rgba')) {
    return value.replace(/rgba\(([^,]+),([^,]+),([^,]+),[^\)]+\)/, `rgba($1,$2,$3,${clamped})`);
  }
  if (value.startsWith('rgb')) {
    return value.replace(/rgb\(([^,]+),([^,]+),([^\)]+)\)/, `rgba($1,$2,$3,${clamped})`);
  }
  return `rgba(15, 23, 42, ${clamped})`;
};

type VideoEmbedConfig =
  | { type: 'iframe'; src: string }
  | { type: 'video'; src: string };

const resolveVideoEmbedConfig = (url?: string | null): VideoEmbedConfig | null => {
  if (!url) return null;
  const trimmed = url.trim();
  if (!trimmed) return null;

  const youtubeMatch =
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{6,})/.exec(trimmed);
  if (youtubeMatch) {
    return { type: 'iframe', src: `https://www.youtube.com/embed/${youtubeMatch[1]}` };
  }

  const vimeoMatch = /vimeo\.com\/(\d+)/.exec(trimmed);
  if (vimeoMatch) {
    return { type: 'iframe', src: `https://player.vimeo.com/video/${vimeoMatch[1]}` };
  }

  if (/\.(mp4|webm|ogg)(?:\?.*)?$/i.test(trimmed)) {
    return { type: 'video', src: trimmed };
  }

  return { type: 'iframe', src: trimmed };
};

const ensureDocument = (document?: LandingDocument | null): LandingDocument => {
  const allowedTypes: BlockType[] = ['heading', 'paragraph', 'image', 'video', 'form', 'background'];
  const base = document ?? createEmptyLandingDocument();
  const candidateBlocks = Array.isArray(base.blocks) ? base.blocks : [];
  const sanitizedBlocks = candidateBlocks
    .map((block) => {
      if (!block) return null;
      const rawType = (block as { type?: string }).type;
      const normalizedType = typeof rawType === 'string' ? rawType.toLowerCase() : '';
      if (!allowedTypes.includes(normalizedType as BlockType)) {
        return null;
      }
      const normalizedBlock = { ...block, type: normalizedType } as LandingBlock;

      if (normalizedType === 'form') {
        const formBlock = normalizedBlock as LandingFormBlock;
        return {
          ...formBlock,
          fields: (formBlock.fields ?? [])
            .filter((field) => field?.id !== 'notes')
            .map((field) => ({
              ...field,
              visible: field?.visible !== false,
              required: field?.required ?? false,
            }))
            .map((field) =>
              field.type === 'email' ? { ...field, visible: true, required: true } : field,
            ),
          background: formBlock.background ?? '#ffffff',
          textColor: formBlock.textColor ?? '#0f172a',
          buttonColor: formBlock.buttonColor ?? '#007AFF',
        } as LandingFormBlock;
      }
      if (normalizedType === 'video') {
        const videoBlock = normalizedBlock as LandingVideoBlock;
        return {
          ...videoBlock,
          url: videoBlock.url ?? '',
          posterUrl: videoBlock.posterUrl ?? null,
          autoplay: videoBlock.autoplay ?? false,
          muted: videoBlock.muted ?? true,
          loop: videoBlock.loop ?? false,
          showControls: videoBlock.showControls ?? true,
          size: videoBlock.size ?? {
            width: 320,
            height: DEFAULT_BLOCK_HEIGHT.video,
          },
        } as LandingVideoBlock;
      }
      if (normalizedType === 'heading' || normalizedType === 'paragraph') {
        const textBlock = normalizedBlock as LandingTextBlock;
        return {
          ...textBlock,
          type: normalizedType,
        } as LandingTextBlock;
      }
      if (normalizedType === 'background') {
        const backgroundBlock = normalizedBlock as LandingBackgroundBlock;
        return {
          ...backgroundBlock,
          color: backgroundBlock.color ?? '#e2e8f0',
          borderRadius: backgroundBlock.borderRadius ?? 0,
          size: backgroundBlock.size ?? {
            width: 360,
            height: DEFAULT_BLOCK_HEIGHT.background,
          },
        } as LandingBackgroundBlock;
      }
      return normalizedBlock;
    })
    .filter((block): block is LandingBlock => Boolean(block));

  return {
    ...base,
    blocks: sanitizedBlocks,
  };
};

const LandingBuilderPage: React.FC = () => {
  // State declarations
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<EditableLanding>({
    name: '',
    slug: '',
    description: '',
    published: false,
    reminderEnabled: false,
    document: createEmptyLandingDocument(),
  });
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuState>(null);
  const [dragState, setDragState] = useState<DragState>(null);
  const [resizeState, setResizeState] = useState<ResizeState>(null);
  const [guides, setGuides] = useState<{ vertical: number | null; horizontal: number | null }>({
    vertical: null,
    horizontal: null,
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [inspectorCollapsed, setInspectorCollapsed] = useState(false);
  const [landingSettingsOpen, setLandingSettingsOpen] = useState(false);
  const [emailSettingsOpen, setEmailSettingsOpen] = useState(false);
  const [backgroundUploading, setBackgroundUploading] = useState(false);
  const [assetUploadingId, setAssetUploadingId] = useState<string | null>(null);
  const [imageSearchOpen, setImageSearchOpen] = useState(false);
  const [imageSearchQuery, setImageSearchQuery] = useState('');
  const [imageSearchResults, setImageSearchResults] = useState<ImageSearchResult[]>([]);
  const [imageSearchLoading, setImageSearchLoading] = useState(false);
  const [imageSearchTarget, setImageSearchTarget] = useState<'background' | { blockId: string }>('background');
  const [customerOptions, setCustomerOptions] = useState<CustomerDTO[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [slugCheck, setSlugCheck] = useState<SlugCheckState>({ status: 'idle' });

  const viewportRef = useRef<HTMLDivElement>(null);

  const blocks = draft.document.blocks ?? [];
  const canvasWidth = draft.document.size?.width ?? 430;
  const blocksRef = useRef<LandingBlock[]>(blocks);
  const slugCheckTimerRef = useRef<number | null>(null);
  const lastSlugKeyRef = useRef<string>('');

  useEffect(() => {
    blocksRef.current = blocks;
  }, [blocks]);

  useEffect(() => {
    const trimmed = (draft.slug ?? '').trim();
    const key = `${trimmed}|${draft.id ?? 'new'}`;

    if (slugCheckTimerRef.current !== null) {
      window.clearTimeout(slugCheckTimerRef.current);
      slugCheckTimerRef.current = null;
    }

    if (!trimmed) {
      lastSlugKeyRef.current = '';
      setSlugCheck({ status: 'idle' });
      return;
    }

    if (key === lastSlugKeyRef.current) {
      return;
    }

    setSlugCheck({ status: 'loading' });

    let cancelled = false;
    const timer = window.setTimeout(() => {
      slugCheckTimerRef.current = null;
      void checkLandingSlugAvailability(trimmed, draft.id ? { excludeId: draft.id } : undefined)
        .then((result) => {
          if (cancelled) {
            return;
          }
          lastSlugKeyRef.current = key;
          const normalizedDiffers = result.slug !== trimmed;
          setSlugCheck({
            status: result.available ? 'available' : 'unavailable',
            normalized: result.slug,
            message: result.available
              ? normalizedDiffers
                ? `Verrà salvato come "${result.slug}"`
                : undefined
              : 'Slug già usato',
          });
        })
        .catch((error: unknown) => {
          if (cancelled) {
            return;
          }
          const message =
            error instanceof Error ? error.message : 'Errore durante la verifica dello slug';
          setSlugCheck({ status: 'error', message });
        });
    }, 400);

    slugCheckTimerRef.current = timer;

    return () => {
      cancelled = true;
      if (slugCheckTimerRef.current !== null) {
        window.clearTimeout(slugCheckTimerRef.current);
        slugCheckTimerRef.current = null;
      }
    };
  }, [draft.slug, draft.id]);

  // Auto-calculate canvas height based on blocks
  const calculatedCanvasHeight = useMemo(() => {
    const MIN_HEIGHT = 932;
    const BOTTOM_PADDING = 200; // Increased from 100 to give more space for footer

    if (blocks.length === 0) {
      return MIN_HEIGHT;
    }

    // Find the lowest point among all blocks
    let maxBottom = 0;
    blocks.forEach((block) => {
      const bottom = block.position.y + (block.size?.height ?? 0);
      if (bottom > maxBottom) {
        maxBottom = bottom;
      }
    });

    // Add padding and ensure minimum height
    const calculatedHeight = Math.max(MIN_HEIGHT, maxBottom + BOTTOM_PADDING);
    return calculatedHeight;
  }, [blocks]);

  const canvasHeight = calculatedCanvasHeight;
  const selectedBlock = blocks.find((b) => b.id === selectedBlockId) ?? null;
  const isFooterSelected = selectedBlockId === FOOTER_BLOCK_ID;
  const defaultFontFamily = resolveFontFamily(draft.document.metadata?.defaultFontFamily);

  const blockMenuItems: MenuProps['items'] = [
    { key: 'heading', label: 'Titolo' },
    { key: 'paragraph', label: 'Paragrafo' },
    { key: 'image', label: 'Immagine' },
    { key: 'video', label: 'Video' },
    { key: 'form', label: 'Form contatti' },
    { key: 'background', label: 'Sfondo colorato' },
  ];
  const blockActionMenuItems: MenuProps['items'] = [
    { key: 'duplicate', label: 'Duplica', icon: <CopyOutlined /> },
    { key: 'forward', label: 'Porta avanti', icon: <ArrowUpOutlined /> },
    { key: 'backward', label: 'Manda indietro', icon: <ArrowDownOutlined /> },
    { type: 'divider' },
    { key: 'delete', label: 'Elimina', icon: <DeleteOutlined />, danger: true },
  ];

    const slugValidateStatus =
    slugCheck.status === 'available'
      ? 'success'
      : slugCheck.status === 'unavailable' || slugCheck.status === 'error'
      ? 'error'
      : slugCheck.status === 'loading'
      ? 'validating'
      : undefined;

  const slugHelpMessage =
    slugCheck.status === 'loading'
      ? 'Verifica disponibilità...'
      : slugCheck.status === 'idle'
      ? undefined
      : slugCheck.message ?? undefined;

  const eventDateValue = useMemo<Dayjs | null>(() => {
    if (!draft.eventDate) return null;
    const parsed = dayjs(draft.eventDate);
    return parsed.isValid() ? parsed : null;
  }, [draft.eventDate]);

  const reminderDateValue = useMemo<Dayjs | null>(() => {
    if (!draft.reminderDate) return null;
    const parsed = dayjs(draft.reminderDate);
    return parsed.isValid() ? parsed : null;
  }, [draft.reminderDate]);

  const disablePastDates = useCallback((current: Dayjs | null) => {
    if (!current) return false;
    return current.endOf('day').isBefore(dayjs().startOf('day'));
  }, []);

  const reminderDisabledDate = useCallback(
    (current: Dayjs | null) => {
      if (!current) return false;
      if (disablePastDates(current)) {
        return true;
      }
      if (eventDateValue) {
        return current.isAfter(eventDateValue);
      }
      return false;
    },
    [disablePastDates, eventDateValue],
  );

  const reminderEnabled = Boolean(draft.reminderEnabled);

  const handleReminderToggle = useCallback((enabled: boolean) => {
    setDraft((prev) => ({
      ...prev,
      reminderEnabled: enabled,
      reminderDate: enabled ? prev.reminderDate : null,
      reminderEmailSubject: enabled ? prev.reminderEmailSubject : null,
      reminderEmailBody: enabled ? prev.reminderEmailBody : null,
    }));
  }, []);

  let slugSuffix: React.ReactNode = null;
  switch (slugCheck.status) {
    case 'loading':
      slugSuffix = <LoadingOutlined spin />;
      break;
    case 'available':
      slugSuffix = <CheckCircleTwoTone twoToneColor="#52c41a" />;
      break;
    case 'unavailable':
      slugSuffix = <CloseCircleTwoTone twoToneColor="#ff4d4f" />;
      break;
    case 'error':
      slugSuffix = (
        <Tooltip title={slugCheck.message ?? 'Errore durante la verifica'}>
          <CloseCircleTwoTone twoToneColor="#ff4d4f" />
        </Tooltip>
      );
      break;
    default:
      slugSuffix = null;
      break;
  }

  const dtoToEditable = (dto: LandingPageDTO): EditableLanding => {
    const doc = ensureDocument(dto.document);
    return {
      id: dto.id,
      name: dto.name,
      slug: dto.slug,
      description: dto.description ?? '',
      customerId: dto.customerId ?? undefined,
      customerName: dto.customerName ?? undefined,
      backgroundColor: dto.backgroundColor ?? undefined,
      backgroundImageUrl: dto.backgroundImageUrl ?? undefined,
      backgroundOverlayColor: dto.backgroundOverlayColor ?? doc.background?.overlayColor ?? undefined,
      backgroundOverlayOpacity: dto.backgroundOverlayOpacity ?? doc.background?.overlayOpacity ?? undefined,
      primaryColor: dto.primaryColor ?? undefined,
      buttonLabel: dto.buttonLabel ?? undefined,
      successMessage: dto.successMessage ?? undefined,
      eventDate: dto.eventDate ?? null,
      reminderDate: dto.reminderDate ?? null,
      reminderEnabled:
        dto.reminderEnabled ?? Boolean(dto.reminderDate || dto.reminderEmailSubject || dto.reminderEmailBody),
      reminderEmailSubject: dto.reminderEmailSubject ?? null,
      reminderEmailBody: dto.reminderEmailBody ?? null,
      couponEmailSubject: dto.couponEmailSubject ?? null,
      couponEmailBody: dto.couponEmailBody ?? null,
      emailSenderName: dto.emailSenderName ?? null,
      published: dto.published,
      document: doc,
    };
  };

  const sanitizePayload = (
    landing: EditableLanding,
    publish: boolean,
    size: { width: number; height: number },
  ): LandingPagePayload => {
    const reminderEnabled = landing.reminderEnabled ?? false;

    return {
      name: landing.name,
      slug: landing.slug,
      description: landing.description,
      customerId: landing.customerId ?? 0,
      backgroundColor: landing.backgroundColor ?? null,
      backgroundImageUrl: landing.backgroundImageUrl ?? null,
      backgroundOverlayColor: landing.backgroundOverlayColor ?? null,
      backgroundOverlayOpacity: landing.backgroundOverlayOpacity ?? null,
      primaryColor: landing.primaryColor ?? null,
      buttonLabel: landing.buttonLabel ?? null,
      successMessage: landing.successMessage ?? null,
      eventDate: landing.eventDate ?? null,
      reminderEnabled,
      reminderDate: reminderEnabled ? landing.reminderDate ?? null : null,
      reminderEmailSubject: reminderEnabled ? landing.reminderEmailSubject?.trim() || null : null,
      reminderEmailBody: reminderEnabled ? landing.reminderEmailBody?.trim() || null : null,
      couponEmailSubject: landing.couponEmailSubject?.trim() || null,
      couponEmailBody: landing.couponEmailBody?.trim() || null,
      emailSenderName: landing.emailSenderName?.trim() || null,
      published: publish,
      document: {
        ...landing.document,
        size,
        background: {
          ...landing.document.background,
          overlayColor: landing.backgroundOverlayColor ?? landing.document.background?.overlayColor ?? null,
          overlayOpacity: landing.backgroundOverlayOpacity ?? landing.document.background?.overlayOpacity ?? null,
        },
      },
    };
  };

  const blockPreviewText = (block: LandingBlock): string => {
    if (block.type === 'heading' || block.type === 'paragraph') {
      const textBlock = block as LandingTextBlock;
      return textBlock.content?.slice(0, 30) ?? '';
    }
  if (block.type === 'image') {
    const imageBlock = block as LandingImageBlock;
    return imageBlock.url ? 'Con immagine' : 'Senza immagine';
  }
  if (block.type === 'video') {
    const videoBlock = block as LandingVideoBlock;
    return videoBlock.url ? 'URL impostato' : 'URL non impostato';
  }
  if (block.type === 'form') {
    const formBlock = block as LandingFormBlock;
    return formBlock.title || 'Form senza titolo';
  }
  if (block.type === 'background') {
    const backgroundBlock = block as LandingBackgroundBlock;
    return backgroundBlock.color ? `Sfondo ${backgroundBlock.color}` : 'Sfondo';
  }
    return '';
  };

  const selectBlock = useCallback((blockId: string | null) => {
    setSelectedBlockId(blockId);
    if (blockId) {
      setInspectorCollapsed(false);
    }
  }, []);

  const closeContextMenu = () => {
    setContextMenu(null);
  };

  const addBlock = (type: BlockType, position: { x: number; y: number }) => {
    let newBlock: LandingBlock;

    if (type === 'heading' || type === 'paragraph') {
      newBlock = {
        id: createId(),
        type,
        position,
        size: {
          width: 320,
          height: DEFAULT_BLOCK_HEIGHT[type],
        },
        zIndex: blocks.length,
        content: type === 'heading' ? 'Nuovo titolo' : 'Nuovo paragrafo',
        align: 'left' as const,
        fontFamily: 'inherit',
        fontSize: type === 'heading' ? 28 : 16,
        fontWeight: type === 'heading' ? 'bold' : 'regular',
        color: '#111827',
      };
    } else if (type === 'image') {
      newBlock = {
        id: createId(),
        type,
        position,
        size: {
          width: 320,
          height: DEFAULT_BLOCK_HEIGHT[type],
        },
        zIndex: blocks.length,
        url: '',
        alt: 'Immagine',
        cornerRadius: 12,
        shadow: false,
      };
    } else if (type === 'video') {
      newBlock = {
        id: createId(),
        type,
        position,
        size: {
          width: 320,
          height: DEFAULT_BLOCK_HEIGHT[type],
        },
        zIndex: blocks.length,
        url: '',
        posterUrl: null,
        autoplay: false,
        muted: true,
        loop: false,
        showControls: true,
      };
    } else if (type === 'background') {
      newBlock = {
        id: createId(),
        type,
        position,
        size: {
          width: 360,
          height: DEFAULT_BLOCK_HEIGHT.background,
        },
        zIndex: blocks.length,
        color: '#e2e8f0',
        borderRadius: 0,
      };
    } else {
      newBlock = {
        id: createId(),
        type,
        position,
        size: {
          width: 320,
          height: DEFAULT_BLOCK_HEIGHT[type],
        },
        zIndex: blocks.length,
        title: 'Contattaci',
        description: 'Compila il form per ricevere maggiori informazioni',
        fields: DEFAULT_FORM_FIELDS,
        buttonLabel: draft.buttonLabel ?? 'Invia',
        successMessage: draft.successMessage ?? 'Grazie per averci contattato!',
        background: '#ffffff',
        textColor: '#0f172a',
        buttonColor: draft.primaryColor ?? '#007AFF',
      };
    }
    setDraft((prev) => ({
      ...prev,
      document: {
        ...prev.document,
        blocks: [...prev.document.blocks, newBlock],
      },
    }));
    selectBlock(newBlock.id);
  };

  const removeBlock = useCallback(
    (blockId: string) => {
      setDraft((prev) => ({
        ...prev,
        document: {
          ...prev.document,
          blocks: prev.document.blocks.filter((b) => b.id !== blockId),
        },
      }));
      if (selectedBlockId === blockId) {
        selectBlock(null);
      }
    },
    [selectedBlockId, selectBlock],
  );

  const duplicateBlock = useCallback(
    (blockId: string) => {
      const original = blocksRef.current.find((block) => block.id === blockId);
      if (!original) {
        return;
      }
      const cloned: LandingBlock = {
        ...original,
        id: createId(),
        position: {
          x: original.position.x + 20,
          y: original.position.y + 20,
        },
        zIndex: blocksRef.current.length,
      };
      setDraft((prev) => ({
        ...prev,
        document: {
          ...prev.document,
          blocks: [...prev.document.blocks, cloned],
        },
      }));
      selectBlock(cloned.id);
    },
    [selectBlock],
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Delete') {
        return;
      }
      const target = event.target as HTMLElement | null;
      if (target) {
        const tagName = target.tagName;
        if (
          target.isContentEditable ||
          tagName === 'INPUT' ||
          tagName === 'TEXTAREA' ||
          tagName === 'SELECT'
        ) {
          return;
        }
      }
      if (!selectedBlockId || selectedBlockId === FOOTER_BLOCK_ID) {
        return;
      }
      event.preventDefault();
      removeBlock(selectedBlockId);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [removeBlock, selectedBlockId]);

  const updateBlock = (blockId: string, updater: (block: LandingBlock) => LandingBlock) => {
    setDraft((prev) => ({
      ...prev,
      document: {
        ...prev.document,
        blocks: prev.document.blocks.map((b) => (b.id === blockId ? updater(b) : b)),
      },
    }));
  };

  const updateFooter = (updater: (prev: LandingFooterSettings) => LandingFooterSettings) => {
    setDraft((prev) => ({
      ...prev,
      document: {
        ...prev.document,
        footer: updater(prev.document.footer),
      },
    }));
  };

  const bumpZIndex = (blockId: string, direction: 'up' | 'down') => {
    setDraft((prev) => {
      const blockIndex = prev.document.blocks.findIndex((b) => b.id === blockId);
      if (blockIndex === -1) return prev;
      const newBlocks = [...prev.document.blocks];
      if (direction === 'up') {
        newBlocks[blockIndex] = { ...newBlocks[blockIndex], zIndex: newBlocks[blockIndex].zIndex + 1 };
      } else {
        newBlocks[blockIndex] = { ...newBlocks[blockIndex], zIndex: Math.max(0, newBlocks[blockIndex].zIndex - 1) };
      }
      return {
        ...prev,
        document: {
          ...prev.document,
          blocks: newBlocks,
        },
      };
    });
  };

  const handlePageFieldChange = <K extends keyof EditableLanding>(
    field: K,
    value: EditableLanding[K],
  ) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handleBackgroundUpload = async (options: RcUploadRequestOption) => {
    const { file, onSuccess, onError } = options;
    setBackgroundUploading(true);
    try {
      const response = await uploadLandingBackground(file as File);
      handlePageFieldChange('backgroundImageUrl', response.url);
      onSuccess?.(response);
      message.success('Sfondo caricato');
    } catch (err: any) {
      message.error(err?.message || 'Errore durante il caricamento');
      onError?.(err);
    } finally {
      setBackgroundUploading(false);
    }
  };

  const handleBlockImageUpload = (blockId: string) => async (options: RcUploadRequestOption) => {
    const { file, onSuccess, onError } = options;
    setAssetUploadingId(blockId);
    try {
      const response = await uploadLandingBlockAsset(file as File);
      updateBlock(blockId, (block) => {
        if (block.type !== 'image') return block;
        return { ...(block as LandingImageBlock), url: response.url };
      });
      onSuccess?.(response);
      message.success('Immagine caricata');
    } catch (err: any) {
      message.error(err?.message || 'Errore durante il caricamento');
      onError?.(err);
    } finally {
      setAssetUploadingId(null);
    }
  };

  const handleBlockVideoUpload = (blockId: string) => async (options: RcUploadRequestOption) => {
    const { file, onSuccess, onError } = options;
    setAssetUploadingId(blockId);
    try {
      const response = await uploadLandingBlockAsset(file as File);
      updateBlock(blockId, (block) => {
        if (block.type !== 'video') return block;
        return { ...(block as LandingVideoBlock), url: response.url };
      });
      onSuccess?.(response);
      message.success('Video caricato');
    } catch (err: any) {
      message.error(err?.message || 'Errore durante il caricamento video');
      onError?.(err);
    } finally {
      setAssetUploadingId(null);
    }
  };

  const performImageSearch = async (query: string) => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      setImageSearchResults([]);
      return;
    }
    setImageSearchLoading(true);
    try {
      const apiResults = await searchImages(trimmedQuery);
      const sanitized = Array.isArray(apiResults)
        ? apiResults.filter(
            (item): item is ImageSearchResult =>
              Boolean(item) && Boolean(item.thumbnail ?? item.full),
          )
        : [];
      const uniqueResults = Array.from(
        new Map(
          sanitized.map((item) => [item.id ?? `${item.thumbnail}-${item.full}`, item]),
        ).values(),
      );

      let finalResults: ImageSearchResult[] = uniqueResults;

      if (finalResults.length === 0) {
        const fallback = createImageFallbackResults(trimmedQuery);
        finalResults = fallback;
        if (fallback.length > 0) {
          message.info('Mostro immagini suggerite in base alla ricerca.');
        } else {
          message.info('Nessuna immagine trovata.');
        }
      } else if (finalResults.length < 12) {
        const fallback = createImageFallbackResults(
          trimmedQuery,
          12 - finalResults.length,
        );
        finalResults = [...finalResults, ...fallback];
      }

      setImageSearchResults(finalResults);
    } catch (err: any) {
      const fallback = createImageFallbackResults(trimmedQuery);
      if (fallback.length > 0) {
        message.warning(
          err?.message
            ? `Ricerca immagini non disponibile (${err.message}). Mostro suggerimenti alternativi.`
            : 'Ricerca immagini non disponibile. Mostro suggerimenti alternativi.',
        );
        setImageSearchResults(fallback);
      } else {
        message.error(err?.message || 'Errore durante la ricerca');
        setImageSearchResults([]);
      }
    } finally {
      setImageSearchLoading(false);
    }
  };

  const loadCustomers = useCallback(async () => {
    setCustomersLoading(true);
    try {
      const customers = await listCustomers();
      const sorted = customers
        .filter((customer) => customer.isActive !== false)
        .sort((a, b) => (a.businessName ?? '').localeCompare(b.businessName ?? '', 'it', { sensitivity: 'base' }));
      setCustomerOptions(sorted);
      setDraft((prev) => {
        if (!prev.customerId) {
          return prev;
        }
        const match = sorted.find((customer) => customer.id === prev.customerId);
        if (!match) {
          return prev;
        }
        if (prev.customerName === match.businessName) {
          return prev;
        }
        return {
          ...prev,
          customerName: match.businessName ?? prev.customerName,
        };
      });
    } catch (err: any) {
      message.error(err?.message || 'Impossibile caricare la lista clienti');
    } finally {
      setCustomersLoading(false);
    }
  }, [setDraft]);
  const handleApplyImageResult = (result: ImageSearchResult) => {
    if (imageSearchTarget === 'background') {
      handlePageFieldChange('backgroundImageUrl', result.full);
    } else {
      const blockId = imageSearchTarget.blockId;
      updateBlock(blockId, (block) => {
        if (block.type !== 'image') return block;
        return { ...(block as LandingImageBlock), url: result.full };
      });
    }
    setImageSearchOpen(false);
    setImageSearchQuery('');
    setImageSearchResults([]);
    message.success('Immagine applicata');
  };

  const openImageSearchForBackground = () => {
    setImageSearchTarget('background');
    setImageSearchOpen(true);
  };

  const openImageSearchForBlock = (blockId: string) => {
    setImageSearchTarget({ blockId });
    setImageSearchOpen(true);
  };

  const startResize = (event: React.MouseEvent, block: LandingBlock) => {
    event.stopPropagation();
    setResizeState({
      id: block.id,
      originX: event.clientX,
      originY: event.clientY,
      startWidth: block.size?.width ?? 320,
      startHeight: block.size?.height ?? 180,
    });
  };

  // Load landing page data
  useEffect(() => {
    let isMounted = true;

    const loadLanding = async () => {
      if (!id) {
        if (isMounted) {
          setLoading(false);
        }
        return;
      }

      if (id === 'new') {
        const stateData = location.state as { customerId?: number; customerName?: string } | null;
        const params = new URLSearchParams(location.search);
        const customerIdFromQuery = params.get('customerId');
        const customerNameFromQuery = params.get('customerName') ?? undefined;

        let resolvedCustomerId: number | undefined;
        if (typeof stateData?.customerId === 'number' && !Number.isNaN(stateData.customerId)) {
          resolvedCustomerId = stateData.customerId;
        } else if (customerIdFromQuery) {
          const parsed = Number(customerIdFromQuery);
          if (!Number.isNaN(parsed)) {
            resolvedCustomerId = parsed;
          }
        }

        const resolvedCustomerName = stateData?.customerName ?? customerNameFromQuery ?? undefined;

        if (isMounted) {
          setDraft((prev) => ({
            ...prev,
            customerId: resolvedCustomerId ?? prev.customerId,
            customerName: resolvedCustomerName ?? prev.customerName,
          }));
          setLoading(false);
        }
        return;
      }

      try {
        const landingId = parseInt(id, 10);
        if (Number.isNaN(landingId)) {
          message.error('ID landing non valido');
          navigate('/landing-pages');
          return;
        }
        const data = await getLandingPage(landingId);
        if (isMounted) {
          setDraft(dtoToEditable(data));
        }
      } catch (err: any) {
        message.error(err?.message || 'Errore durante il caricamento');
        navigate('/landing-pages');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    void loadLanding();

    return () => {
      isMounted = false;
    };
  }, [id, navigate, location.state, location.search]);

  // Handle drag
  useEffect(() => {
    if (!dragState) return;

    const viewport = viewportRef.current;
    if (!viewport) return;

    const block = blocksRef.current.find((b) => b.id === dragState.id);
    const blockType = (block?.type as BlockType | undefined) ?? undefined;
    const fallbackHeight =
      (blockType && DEFAULT_BLOCK_HEIGHT[blockType]) ?? DEFAULT_BLOCK_HEIGHT.paragraph;

    const blockWidth = block?.size?.width ?? 320;
    const blockHeight = block?.size?.height ?? fallbackHeight;

    const bounds = {
      minX: 0,
      maxX: Math.max(canvasWidth - blockWidth, 0),
      minY: 0,
      maxY: Math.max(canvasHeight - blockHeight, 0),
    };

    const canvasCenterX = canvasWidth / 2;
    const canvasCenterY = canvasHeight / 2;

    const handleMouseMove = (event: MouseEvent) => {
      const rect = viewport.getBoundingClientRect();
      const scrollLeft = viewport.scrollLeft;
      const scrollTop = viewport.scrollTop;

      let nextX = event.clientX - rect.left + scrollLeft - dragState.offsetX;
      let nextY = event.clientY - rect.top + scrollTop - dragState.offsetY;

      nextX = Math.min(Math.max(nextX, bounds.minX), bounds.maxX);
      nextY = Math.min(Math.max(nextY, bounds.minY), bounds.maxY);

      let verticalGuide: number | null = null;
      let horizontalGuide: number | null = null;

      const centerX = nextX + blockWidth / 2;
      if (Math.abs(centerX - canvasCenterX) <= CENTER_SNAP_TOLERANCE) {
        const snappedX = canvasCenterX - blockWidth / 2;
        const clampedX = Math.min(Math.max(snappedX, bounds.minX), bounds.maxX);
        if (Math.abs(clampedX + blockWidth / 2 - canvasCenterX) <= 0.5) {
          nextX = clampedX;
          verticalGuide = canvasCenterX;
        }
      }

      const centerY = nextY + blockHeight / 2;
      if (Math.abs(centerY - canvasCenterY) <= CENTER_SNAP_TOLERANCE) {
        const snappedY = canvasCenterY - blockHeight / 2;
        const clampedY = Math.min(Math.max(snappedY, bounds.minY), bounds.maxY);
        if (Math.abs(clampedY + blockHeight / 2 - canvasCenterY) <= 0.5) {
          nextY = clampedY;
          horizontalGuide = canvasCenterY;
        }
      }

      updateBlock(dragState.id, (block) => ({
        ...block,
        position: { x: nextX, y: nextY },
      }));
      setGuides({ vertical: verticalGuide, horizontal: horizontalGuide });
    };

    const handleMouseUp = () => {
      setDragState(null);
      setGuides({ vertical: null, horizontal: null });
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState, canvasWidth, canvasHeight]);

  // Handle resize
  useEffect(() => {
    if (!resizeState) return;

    const handleMouseMove = (event: MouseEvent) => {
      const deltaX = event.clientX - resizeState.originX;
      const deltaY = event.clientY - resizeState.originY;
      const newWidth = Math.max(120, resizeState.startWidth + deltaX);
      const newHeight = Math.max(40, resizeState.startHeight + deltaY);
      updateBlock(resizeState.id, (block) => ({
        ...block,
        size: {
          width: newWidth,
          height: newHeight,
        },
      }));
    };

    const handleMouseUp = () => {
      setResizeState(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizeState]);

  // Auto-open settings drawer on first load
  useEffect(() => {
    if (!loading) {
      setLandingSettingsOpen(true);
    }
  }, [loading]);

  useEffect(() => {
    if (!landingSettingsOpen) {
      return;
    }

    if (customerOptions.length === 0 && !customersLoading) {
      void loadCustomers();
      return;
    }

    if (!draft.customerId || customerOptions.length === 0) {
      return;
    }

    const match = customerOptions.find((customer) => customer.id === draft.customerId);
    if (match && draft.customerName !== match.businessName) {
      setDraft((prev) => ({
        ...prev,
        customerName: match.businessName ?? prev.customerName,
      }));
    }
  }, [
    customerOptions,
    customersLoading,
    draft.customerId,
    draft.customerName,
    loadCustomers,
    landingSettingsOpen,
  ]);
  const renderBlock = (block: LandingBlock) => {
    const isSelected = block.id === selectedBlockId;
    const style: React.CSSProperties = {
      position: 'absolute',
      left: block.position.x,
      top: block.position.y,
      width: block.size?.width ?? 320,
      height: block.size?.height ?? 80,
      zIndex: block.zIndex ?? 0,
    };

    let content: React.ReactNode;

    if (block.type === 'heading' || block.type === 'paragraph') {
      const textBlock = block as LandingTextBlock;
      const fontFamilyValue =
        textBlock.fontFamily && textBlock.fontFamily !== 'inherit'
          ? textBlock.fontFamily
          : defaultFontFamily;
      const fontSize = textBlock.fontSize ?? (textBlock.type === 'heading' ? 28 : 16);
      const weight = textBlock.fontWeight ?? (textBlock.type === 'heading' ? 'bold' : 'regular');
      const lineHeight = textBlock.lineHeight ?? (textBlock.type === 'heading' ? 1.2 : 1.6);

      content = (
        <div
          className="landing-builder__text-block"
          style={{
            textAlign: textBlock.align ?? 'left',
            fontSize,
            fontWeight: weight === 'bold' ? 700 : weight === 'medium' ? 600 : 400,
            color: textBlock.color ?? '#111827',
            lineHeight,
            fontFamily: fontFamilyValue,
            maxWidth: textBlock.size?.width ?? 320,
          }}
          onDoubleClick={(event) => {
            event.stopPropagation();
            selectBlock(block.id);
          }}
        >
          {textBlock.content?.trim() ? textBlock.content : 'Testo'}
        </div>
      );
    } else if (block.type === 'background') {
      const backgroundBlock = block as LandingBackgroundBlock;
      content = (
        <div
          className="landing-builder__background-block"
          style={{
            background: backgroundBlock.color ?? '#e2e8f0',
            borderRadius: backgroundBlock.borderRadius ?? 0,
          }}
        />
      );
    } else if (block.type === 'image') {
      const imageBlock = block as LandingImageBlock;
      const hasUrl = Boolean(imageBlock.url?.trim());
      content = hasUrl ? (
        <img
          src={imageBlock.url ?? ''}
          alt={imageBlock.alt ?? 'Immagine'}
          style={{
            width: '100%',
            height: '100%',
            objectFit: imageBlock.objectFit ?? 'cover',
            borderRadius: imageBlock.cornerRadius ?? 12,
            boxShadow: imageBlock.shadow ? '0 18px 40px rgba(15, 23, 42, 0.18)' : 'none',
          }}
          draggable={false}
        />
      ) : (
        <div className="landing-builder__image-placeholder">
          <Text strong>Immagine</Text>
          <Text type="secondary">Imposta un URL nell&apos;ispettore</Text>
        </div>
      );
    } else if (block.type === 'video') {
      const videoBlock = block as LandingVideoBlock;
      const embedConfig = resolveVideoEmbedConfig(videoBlock.url);
      const hasUrl = Boolean(videoBlock.url?.trim());
      content = (
        <div className="landing-builder__video-preview">
          {embedConfig ? (
            embedConfig.type === 'iframe' ? (
              <iframe
                title="Anteprima video"
                src={embedConfig.src}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <video
                src={embedConfig.src}
                poster={videoBlock.posterUrl ?? undefined}
                controls={videoBlock.showControls !== false}
                autoPlay={Boolean(videoBlock.autoplay)}
                muted={videoBlock.muted !== false}
                loop={Boolean(videoBlock.loop)}
              />
            )
          ) : hasUrl ? (
            <div className="landing-builder__video-fallback">
              <Text strong>Anteprima non disponibile</Text>
              <Text type="secondary">{videoBlock.url}</Text>
            </div>
          ) : (
            <div className="landing-builder__video-placeholder">
              <Text strong>Video</Text>
              <Text type="secondary">Imposta un URL nell&apos;ispettore</Text>
            </div>
          )}
        </div>
      );
    } else if (block.type === 'form') {
      const formBlock = block as LandingFormBlock;
      content = (
        <div
          className="landing-builder__form"
          style={{
            background: formBlock.background ?? '#ffffff',
            color: formBlock.textColor ?? '#0f172a',
            borderRadius: formBlock.borderRadius ?? 16,
            fontFamily: defaultFontFamily,
          }}
        >
          {formBlock.title ? <h3 style={{ fontFamily: defaultFontFamily }}>{formBlock.title}</h3> : null}
          {formBlock.description ? <p style={{ fontFamily: defaultFontFamily }}>{formBlock.description}</p> : null}
          <form>
            {formBlock.fields
              .filter((field) => field.visible !== false)
              .map((field) => {
                const key = `${formBlock.id}-${field.id}`;
                if (field.type === 'textarea') {
                  return (
                    <TextArea
                      key={key}
                      placeholder={field.placeholder ?? ''}
                      autoSize={{ minRows: 3, maxRows: 4 }}
                      disabled
                    />
                  );
                }
                return (
                  <Input
                    key={key}
                    placeholder={field.placeholder ?? ''}
                    type={field.type === 'email' ? 'email' : 'text'}
                    disabled
                  />
                );
              })}
            <Checkbox disabled style={{ marginTop: 12 }}>
              Accetto l'
              <span style={{ textDecoration: 'underline' }}>informativa privacy</span>
            </Checkbox>
            <Button
              type="primary"
              block
              style={{
                background: formBlock.buttonColor ?? draft.primaryColor ?? '#007AFF',
                borderColor: formBlock.buttonColor ?? draft.primaryColor ?? '#007AFF',
                fontFamily: defaultFontFamily,
              }}
            >
              {formBlock.buttonLabel || 'Invia'}
            </Button>
          </form>
        </div>
      );
    }

    return (
      <div
        key={block.id}
        className={[
          'landing-builder__block',
          `landing-builder__block--${block.type}`,
          isSelected ? 'landing-builder__block--selected' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={style}
        onMouseDown={(event) => {
          if (event.button !== 0) return;
          event.stopPropagation();
          const viewport = viewportRef.current;
          if (!viewport) return;
          const rect = viewport.getBoundingClientRect();
          const scrollLeft = viewport.scrollLeft;
          const scrollTop = viewport.scrollTop;
          const offsetX = event.clientX - (rect.left + block.position.x - scrollLeft);
          const offsetY = event.clientY - (rect.top + block.position.y - scrollTop);
          selectBlock(block.id);
          setDragState({ id: block.id, offsetX, offsetY });
        }}
        onClick={(event) => {
          event.stopPropagation();
          selectBlock(block.id);
        }}
        onContextMenu={(event) => {
          event.preventDefault();
          event.stopPropagation();
          const viewport = viewportRef.current;
          if (!viewport) return;
          const rect = viewport.getBoundingClientRect();
          const x = event.clientX - rect.left + viewport.scrollLeft;
          const y = event.clientY - rect.top + viewport.scrollTop;
          selectBlock(block.id);
          setContextMenu({ x, y, blockId: block.id });
        }}
      >
        {content}
        <div className="landing-builder__block-badge">{BLOCK_LABEL[block.type as BlockType]}</div>
        {isSelected && (
          <div
            className="landing-builder__resize-handle landing-builder__resize-handle--se"
            onMouseDown={(event) => startResize(event, block)}
          />
        )}
      </div>
    );
  };

  const renderFooterPreview = () => {
    const footer = draft.document.footer;
    const lines = footer.content.split(/\r?\r\n/);
    const footerSelected = isFooterSelected;
    return (
      <div
        className={[
          'landing-builder__footer-fixed',
          footerSelected ? 'landing-builder__footer-fixed--selected' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        style={{
          background: footer.background ?? '#e2e8f0',
          color: footer.textColor ?? '#475569',
          textAlign: footer.align ?? 'center',
          fontSize: footer.fontSize ?? 14,
          fontFamily: footer.fontFamily ?? defaultFontFamily,
          gap: footer.spacing ?? 12,
        }}
        onClick={(event) => {
          event.stopPropagation();
          selectBlock(FOOTER_BLOCK_ID);
        }}
      >
        {lines.map((line, index) => (
          <span key={index}>{line || '\u00A0'}</span>
        ))}
      </div>
    );
  };

  const handleContextMenu: React.MouseEventHandler<HTMLDivElement> = (event) => {
    event.preventDefault();
    const viewport = viewportRef.current;
    if (!viewport) return;
    const rect = viewport.getBoundingClientRect();
    const x = event.clientX - rect.left + viewport.scrollLeft;
    const y = event.clientY - rect.top + viewport.scrollTop;
    setContextMenu({ x, y });
  };

  const handleSave = async (publish: boolean) => {
    if (!draft.name.trim()) {
      message.warning('Inserisci il nome della landing');
      return;
    }
  if (!draft.slug.trim()) {
    message.warning('Inserisci lo slug (URL)');
    return;
  }
  if (slugCheck.status === 'loading') {
    message.warning('Attendi il completamento della verifica dello slug');
    return;
  }
  if (slugCheck.status === 'unavailable') {
    message.error(slugCheck.message ?? 'Slug già in uso, scegline un altro');
    return;
  }
  if (draft.customerId === undefined || draft.customerId === null) {
    message.warning('Associa un cliente prima di procedere');
    return;
  }
  setSaving(true);
    try {
      const payload = sanitizePayload(draft, publish, {
        width: canvasWidth,
        height: canvasHeight,
      });
      let response: LandingPageDTO;
      if (draft.id) {
        response = await updateLandingPage(draft.id, payload);
      } else {
        response = await createLandingPage(payload);
        if (response.id) {
          navigate(`/landing-builder/${response.id}`, { replace: true });
        }
      }
      setDraft(dtoToEditable(response));
      message.success(publish ? 'Landing pubblicata' : 'Bozza salvata');
    } catch (err: any) {
      const rawMessage = typeof err?.message === 'string' ? err.message : '';
      const normalized = rawMessage.toLowerCase();
      if (normalized.includes('slug') || normalized.includes('duplicate entry')) {
        message.error('Slug già in uso, scegline un altro');
        setSlugCheck({
          status: 'unavailable',
          normalized: slugCheck.normalized ?? draft.slug,
          message: 'Slug già in uso',
        });
      } else if (normalized.includes('internal server error')) {
        message.error('Errore del server. Riprova più tardi.');
      } else {
        message.error(rawMessage || 'Errore durante il salvataggio');
      }
    } finally {
      setSaving(false);
    }
  };

  const onBlockMenuClick: MenuProps['onClick'] = ({ key }) => {
    const blockType = key as BlockType;
    addBlock(blockType, {
      x: 56,
      y: blocks.length ? blocks[blocks.length - 1].position.y + 140 : 56,
    });
  };

  const renderInspector = () => {
    if (isFooterSelected) {
      const footer = draft.document.footer;
      const handleFooterChange = (patch: Partial<LandingFooterSettings>) =>
        updateFooter((prev) => ({ ...prev, ...patch }));
      return (
        <div className="landing-builder__inspector-content">
          <Title level={5}>Footer</Title>
          <div className="landing-builder__inspector-row">
            <Text strong>Contenuto</Text>
            <TextArea
              value={footer.content}
              autoSize={{ minRows: 3, maxRows: 6 }}
              onChange={(event) =>
                handleFooterChange({ content: event.target.value })
              }
            />
          </div>

          <Divider />
          <Title level={5}>Stile</Title>
          <div className="landing-builder__inspector-row">
            <Text strong>Allineamento</Text>
            <Segmented
              block
              value={footer.align ?? 'center'}
              options={[
                { label: 'Sinistra', value: 'left' },
                { label: 'Centro', value: 'center' },
                { label: 'Destra', value: 'right' },
              ]}
              onChange={(value) =>
                handleFooterChange({
                  align: value as LandingFooterSettings['align'],
                })
              }
            />
          </div>
          <div className="landing-builder__inspector-row">
            <Text strong>Font</Text>
            <Select
              style={{ width: '100%' }}
              options={FONT_OPTIONS}
              value={footer.fontFamily ?? 'inherit'}
              onChange={(value) => handleFooterChange({ fontFamily: value })}
            />
          </div>
          <div className="landing-builder__inspector-row">
            <Text strong>Dimensione testo</Text>
            <Slider
              min={10}
              max={32}
              value={footer.fontSize ?? 14}
              onChange={(value) =>
                handleFooterChange({ fontSize: value as number })
              }
            />
          </div>
          <div className="landing-builder__inspector-row">
            <Text strong>Colore testo</Text>
            <Space>
              <ColorPicker
                value={footer.textColor ?? '#475569'}
                allowClear
                onChange={(_, hex) =>
                  handleFooterChange({ textColor: hex ?? '' })
                }
              />
              <Input
                value={footer.textColor ?? ''}
                placeholder="#475569"
                onChange={(event) =>
                  handleFooterChange({ textColor: event.target.value })
                }
              />
            </Space>
          </div>
          <div className="landing-builder__inspector-row">
            <Text strong>Colore sfondo</Text>
            <Space>
              <ColorPicker
                value={footer.background ?? '#e2e8f0'}
                allowClear
                onChange={(_, hex) =>
                  handleFooterChange({ background: hex ?? '' })
                }
              />
              <Input
                value={footer.background ?? ''}
                placeholder="#e2e8f0"
                onChange={(event) =>
                  handleFooterChange({ background: event.target.value })
                }
              />
            </Space>
          </div>
          <div className="landing-builder__inspector-row">
            <Text strong>Spaziatura</Text>
            <Slider
              min={4}
              max={48}
              value={footer.spacing ?? 12}
              onChange={(value) =>
                handleFooterChange({ spacing: value as number })
              }
            />
          </div>
        </div>
      );
    }

    if (!selectedBlock) {
      return (
        <div className="landing-builder__inspector-content">
          <Empty
            description={<span>Seleziona un blocco o fai click destro sul canvas per aggiungerne uno nuovo</span>}
          />
          <Divider />
          <Title level={5}>Footer</Title>
          <Text type="secondary">Personalizza il testo e lo stile del piede di pagina della landing.</Text>
          <Button
            icon={<SettingOutlined />}
            onClick={() => selectBlock(FOOTER_BLOCK_ID)}
            block
          >
            Modifica footer
          </Button>
        </div>
      );
    }

    if (selectedBlock.type === 'heading' || selectedBlock.type === 'paragraph') {
      const textBlock = selectedBlock as LandingTextBlock;
      const fontSize =
        textBlock.fontSize ?? (textBlock.type === 'heading' ? 28 : 16);
      return (
        <div className="landing-builder__inspector-content">
          <Title level={5}>Testo</Title>
          <div className="landing-builder__inspector-row">
            <Text strong>Contenuto</Text>
            <TextArea
              value={textBlock.content}
              autoSize={{ minRows: 3, maxRows: 6 }}
              onChange={(event) =>
                updateBlock(textBlock.id, (block) => ({
                  ...block,
                  content: event.target.value,
                }))
              }
            />
          </div>

          <Divider />
          <Title level={5}>Stile</Title>
          <div className="landing-builder__inspector-row">
            <Text strong>Font</Text>
            <Select
              style={{ width: '100%' }}
              options={FONT_OPTIONS}
              value={textBlock.fontFamily ?? 'inherit'}
              onChange={(value) =>
                updateBlock(textBlock.id, (block) => ({
                  ...block,
                  fontFamily: value,
                }))
              }
            />
          </div>
          <div className="landing-builder__inspector-row">
            <Text strong>Spessore testo</Text>
            <Segmented
              block
              value={textBlock.fontWeight ?? (textBlock.type === 'heading' ? 'bold' : 'regular')}
              options={[
                { label: 'Regular', value: 'regular' },
                { label: 'Medium', value: 'medium' },
                { label: 'Grassetto', value: 'bold' },
              ]}
              onChange={(value) =>
                updateBlock(textBlock.id, (block) => ({
                  ...block,
                  fontWeight: value as LandingTextBlock['fontWeight'],
                }))
              }
            />
          </div>
          <div className="landing-builder__inspector-row">
            <Text strong>Allineamento</Text>
            <Segmented
              block
              value={textBlock.align ?? 'left'}
              options={[
                { label: 'Sinistra', value: 'left' },
                { label: 'Centro', value: 'center' },
                { label: 'Destra', value: 'right' },
              ]}
              onChange={(value) =>
                updateBlock(textBlock.id, (block) => ({
                  ...block,
                  align: value as LandingTextBlock['align'],
                }))
              }
            />
          </div>
          <div className="landing-builder__inspector-row">
            <Text strong>Dimensione testo</Text>
            <Slider
              min={12}
              max={54}
              value={fontSize}
              onChange={(value) =>
                updateBlock(textBlock.id, (block) => ({
                  ...block,
                  fontSize: value as number,
                }))
              }
            />
          </div>
          <div className="landing-builder__inspector-row">
            <Text strong>Colore</Text>
            <Space align="center">
              <ColorPicker
                value={textBlock.color ?? '#111827'}
                allowClear
                onChange={(_, hex) =>
                  updateBlock(textBlock.id, (block) => ({
                    ...block,
                    color: hex ?? '',
                  }))
                }
              />
              <Input
                value={textBlock.color ?? ''}
                placeholder="#111827"
                onChange={(event) =>
                  updateBlock(textBlock.id, (block) => ({
                    ...block,
                    color: event.target.value,
                  }))
                }
              />
            </Space>
          </div>
          <div className="landing-builder__inspector-row">
            <Text strong>Larghezza blocco</Text>
            <InputNumber
              min={120}
              max={380}
              value={textBlock.size?.width ?? 320}
              onChange={(value) =>
                updateBlock(textBlock.id, (block) => ({
                  ...block,
                  size: {
                    ...(block.size ?? { height: 80 }),
                    width: value ?? 320,
                  },
                }))
              }
            />
          </div>

          <Divider />
          <Space direction="vertical" style={{ width: '100%' }}>
            <Button
              icon={<CopyOutlined />}
              block
              onClick={() => duplicateBlock(textBlock.id)}
            >
              Duplica blocco
            </Button>
            <Button
              icon={<DeleteOutlined />}
              danger
              block
              onClick={() => removeBlock(textBlock.id)}
            >
              Elimina blocco
            </Button>
            <Button onClick={() => bumpZIndex(textBlock.id, 'up')} block>
              Porta avanti
            </Button>
            <Button onClick={() => bumpZIndex(textBlock.id, 'down')} block>
              Manda indietro
            </Button>
          </Space>
        </div>
      );
    }

    if (selectedBlock.type === 'background') {
      const backgroundBlock = selectedBlock as LandingBackgroundBlock;
      return (
        <div className="landing-builder__inspector-content">
          <Title level={5}>Sfondo colorato</Title>
          <div className="landing-builder__inspector-row">
            <Text strong>Colore</Text>
            <Space align="center">
              <ColorPicker
                value={backgroundBlock.color ?? '#e2e8f0'}
                allowClear
                onChange={(_, hex) =>
                  updateBlock(backgroundBlock.id, (block) => ({
                    ...block,
                    color: hex ?? '',
                  }))
                }
              />
              <Input
                value={backgroundBlock.color ?? ''}
                placeholder="#e2e8f0"
                onChange={(event) =>
                  updateBlock(backgroundBlock.id, (block) => ({
                    ...block,
                    color: event.target.value,
                  }))
                }
              />
            </Space>
          </div>
          <div className="landing-builder__inspector-row">
            <Text strong>Raggio angoli</Text>
            <InputNumber
              min={0}
              max={48}
              value={backgroundBlock.borderRadius ?? 0}
              onChange={(value) =>
                updateBlock(backgroundBlock.id, (block) => ({
                  ...block,
                  borderRadius: value ?? 0,
                }))
              }
            />
          </div>
          <Divider />
          <Title level={5}>Dimensioni</Title>
          <div className="landing-builder__inspector-row">
            <Text strong>Larghezza</Text>
            <InputNumber
              min={160}
              max={420}
              value={backgroundBlock.size?.width ?? 360}
              onChange={(value) =>
                updateBlock(backgroundBlock.id, (block) => ({
                  ...block,
                  size: {
                    width: value ?? 360,
                    height: block.size?.height ?? DEFAULT_BLOCK_HEIGHT.background,
                  },
                }))
              }
            />
          </div>
          <div className="landing-builder__inspector-row">
            <Text strong>Altezza</Text>
            <InputNumber
              min={80}
              max={1200}
              value={backgroundBlock.size?.height ?? DEFAULT_BLOCK_HEIGHT.background}
              onChange={(value) =>
                updateBlock(backgroundBlock.id, (block) => ({
                  ...block,
                  size: {
                    width: block.size?.width ?? 360,
                    height: value ?? DEFAULT_BLOCK_HEIGHT.background,
                  },
                }))
              }
            />
          </div>
          <Divider />
          <Space direction="vertical" style={{ width: '100%' }}>
            <Button
              icon={<CopyOutlined />}
              block
              onClick={() => duplicateBlock(backgroundBlock.id)}
            >
              Duplica blocco
            </Button>
            <Button
              icon={<DeleteOutlined />}
              danger
              block
              onClick={() => removeBlock(backgroundBlock.id)}
            >
              Elimina blocco
            </Button>
            <Button onClick={() => bumpZIndex(backgroundBlock.id, 'up')} block>
              Porta avanti
            </Button>
            <Button onClick={() => bumpZIndex(backgroundBlock.id, 'down')} block>
              Manda indietro
            </Button>
          </Space>
        </div>
      );
    }

    if (selectedBlock.type === 'image') {
      const imageBlock = selectedBlock as LandingImageBlock;
      return (
        <div className="landing-builder__inspector-content">
          <Title level={5}>Immagine</Title>
          <div className="landing-builder__inspector-row">
            <Text strong>URL immagine</Text>
            <Input
              value={imageBlock.url ?? ''}
              placeholder="https://..."
              onChange={(event) =>
                updateBlock(imageBlock.id, (block) => ({
                  ...block,
                  url: event.target.value,
                }))
              }
            />
            <Text type="secondary">
              Usa un URL pubblico o carica il file dal tuo pc.
            </Text>
          </div>
          <Space style={{ marginTop: 12 }} wrap>
            <Upload
              accept="image/*"
              showUploadList={false}
              customRequest={handleBlockImageUpload(imageBlock.id)}
            >
              <Button
                icon={<UploadOutlined />}
                loading={assetUploadingId === imageBlock.id}
              >
                Carica immagine
              </Button>
            </Upload>
            <Button
              icon={<SearchOutlined />}
              onClick={() => openImageSearchForBlock(imageBlock.id)}
            >
              Cerca sul web
            </Button>
            {imageBlock.url ? (
              <Button
                onClick={() =>
                  updateBlock(imageBlock.id, (block) => {
                    if (block.type !== 'image') return block;
                    return { ...(block as LandingImageBlock), url: '' };
                  })
                }
              >
                Rimuovi immagine
              </Button>
            ) : null}
          </Space>

          <Divider />
          <Title level={5}>Layout</Title>
          <div className="landing-builder__inspector-row">
            <Text strong>Larghezza</Text>
            <InputNumber
              min={140}
              max={380}
              value={imageBlock.size?.width ?? 320}
              onChange={(value) =>
                updateBlock(imageBlock.id, (block) => ({
                  ...block,
                  size: {
                    width: value ?? 320,
                    height: block.size?.height ?? 180,
                  },
                }))
              }
            />
          </div>
          <div className="landing-builder__inspector-row">
            <Text strong>Altezza</Text>
            <InputNumber
              min={80}
              max={640}
              value={imageBlock.size?.height ?? 180}
              onChange={(value) =>
                updateBlock(imageBlock.id, (block) => ({
                  ...block,
                  size: {
                    width: block.size?.width ?? 320,
                    height: value ?? 180,
                  },
                }))
              }
            />
          </div>
          <div className="landing-builder__inspector-row">
            <Text strong>Raggio angoli</Text>
            <InputNumber
              min={0}
              max={48}
              value={imageBlock.cornerRadius ?? 16}
              onChange={(value) =>
                updateBlock(imageBlock.id, (block) => ({
                  ...block,
                  cornerRadius: value ?? 0,
                }))
              }
            />
          </div>
          <div className="landing-builder__inspector-row landing-builder__inspector-row--inline">
            <Text strong>Ombra</Text>
            <Switch
              checked={Boolean(imageBlock.shadow)}
              onChange={(checked) =>
                updateBlock(imageBlock.id, (block) => ({
                  ...block,
                  shadow: checked,
                }))
              }
            />
          </div>

          <Divider />
          <Space direction="vertical" style={{ width: '100%' }}>
            <Button
              icon={<CopyOutlined />}
              block
              onClick={() => duplicateBlock(imageBlock.id)}
            >
              Duplica blocco
            </Button>
            <Button
              icon={<DeleteOutlined />}
              danger
              block
              onClick={() => removeBlock(imageBlock.id)}
            >
              Elimina blocco
            </Button>
            <Button onClick={() => bumpZIndex(imageBlock.id, 'up')} block>
              Porta avanti
            </Button>
            <Button onClick={() => bumpZIndex(imageBlock.id, 'down')} block>
              Manda indietro
            </Button>
          </Space>
        </div>
      );
    }

    if (selectedBlock.type === 'video') {
      const videoBlock = selectedBlock as LandingVideoBlock;
      return (
        <div className="landing-builder__inspector-content">
          <Title level={5}>Video</Title>
          <div className="landing-builder__inspector-row">
            <Text strong>URL video</Text>
            <Input
              value={videoBlock.url ?? ''}
              placeholder="https://..."
              onChange={(event) =>
                updateBlock(videoBlock.id, (block) => ({
                  ...block,
                  url: event.target.value,
                }))
              }
            />
            <Text type="secondary">
              Supportati link di YouTube.
            </Text>
          </div>
          <Space style={{ marginTop: 12 }} wrap>
            <Upload
              accept="video/*"
              showUploadList={false}
              customRequest={handleBlockVideoUpload(videoBlock.id)}
            >
              <Button
                icon={<UploadOutlined />}
                loading={assetUploadingId === videoBlock.id}
              >
                Carica video
              </Button>
            </Upload>
            {videoBlock.url ? (
              <Button
                onClick={() =>
                  updateBlock(videoBlock.id, (block) => {
                    if (block.type !== 'video') return block;
                    return { ...(block as LandingVideoBlock), url: '' };
                  })
                }
              >
                Rimuovi video
              </Button>
            ) : null}
          </Space>
       

          <Divider />
          <Title level={5}>Riproduzione</Title>
          <div className="landing-builder__inspector-row landing-builder__inspector-row--inline">
            <Text strong>Autoplay</Text>
            <Switch
              checked={Boolean(videoBlock.autoplay)}
              onChange={(checked) =>
                updateBlock(videoBlock.id, (block) => ({
                  ...block,
                  autoplay: checked,
                }))
              }
            />
          </div>
          <div className="landing-builder__inspector-row landing-builder__inspector-row--inline">
            <Text strong>Muto</Text>
            <Switch
              checked={videoBlock.muted !== false}
              onChange={(checked) =>
                updateBlock(videoBlock.id, (block) => ({
                  ...block,
                  muted: checked,
                }))
              }
            />
          </div>
          <div className="landing-builder__inspector-row landing-builder__inspector-row--inline">
            <Text strong>Loop</Text>
            <Switch
              checked={Boolean(videoBlock.loop)}
              onChange={(checked) =>
                updateBlock(videoBlock.id, (block) => ({
                  ...block,
                  loop: checked,
                }))
              }
            />
          </div>
          <div className="landing-builder__inspector-row landing-builder__inspector-row--inline">
            <Text strong>Mostra controlli</Text>
            <Switch
              checked={videoBlock.showControls !== false}
              onChange={(checked) =>
                updateBlock(videoBlock.id, (block) => ({
                  ...block,
                  showControls: checked,
                }))
              }
            />
          </div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            Per l&apos;autoplay i browser richiedono solitamente che il video sia silenziato.
          </Text>

          <Divider />
          <Title level={5}>Dimensioni</Title>
          <div className="landing-builder__inspector-row">
            <Text strong>Larghezza</Text>
            <InputNumber
              min={200}
              max={380}
              value={videoBlock.size?.width ?? 320}
              onChange={(value) =>
                updateBlock(videoBlock.id, (block) => ({
                  ...block,
                  size: {
                    width: value ?? 320,
                    height: block.size?.height ?? 200,
                  },
                }))
              }
            />
          </div>
          <div className="landing-builder__inspector-row">
            <Text strong>Altezza</Text>
            <InputNumber
              min={120}
              max={640}
              value={videoBlock.size?.height ?? 200}
              onChange={(value) =>
                updateBlock(videoBlock.id, (block) => ({
                  ...block,
                  size: {
                    width: block.size?.width ?? 320,
                    height: value ?? 200,
                  },
                }))
              }
            />
          </div>

          <Divider />
          <Space direction="vertical" style={{ width: '100%' }}>
            <Button
              icon={<CopyOutlined />}
              block
              onClick={() => duplicateBlock(videoBlock.id)}
            >
              Duplica blocco
            </Button>
            <Button
              icon={<DeleteOutlined />}
              danger
              block
              onClick={() => removeBlock(videoBlock.id)}
            >
              Elimina blocco
            </Button>
            <Button onClick={() => bumpZIndex(videoBlock.id, 'up')} block>
              Porta avanti
            </Button>
            <Button onClick={() => bumpZIndex(videoBlock.id, 'down')} block>
              Manda indietro
            </Button>
          </Space>
        </div>
      );
    }

    if (selectedBlock.type === 'form') {
      const formBlock = selectedBlock as LandingFormBlock;
      return (
        <div className="landing-builder__inspector-content">
          <Title level={5}>Form contatti</Title>
          <div className="landing-builder__inspector-row">
            <Text strong>Titolo</Text>
            <Input
              value={formBlock.title ?? ''}
              placeholder="Titolo form"
              onChange={(event) =>
                updateBlock(formBlock.id, (block) => ({
                  ...block,
                  title: event.target.value,
                }))
              }
            />
          </div>
          <div className="landing-builder__inspector-row">
            <Text strong>Descrizione</Text>
            <TextArea
              value={formBlock.description ?? ''}
              placeholder="Descrizione"
              autoSize={{ minRows: 2, maxRows: 4 }}
              onChange={(event) =>
                updateBlock(formBlock.id, (block) => ({
                  ...block,
                  description: event.target.value,
                }))
              }
            />
          </div>
          <div className="landing-builder__inspector-row">
            <Text strong>Testo pulsante</Text>
            <Input
              value={formBlock.buttonLabel ?? draft.buttonLabel ?? 'Invia'}
              placeholder="Testo pulsante"
              onChange={(event) => {
                handlePageFieldChange('buttonLabel', event.target.value);
                updateBlock(formBlock.id, (block) => ({
                  ...block,
                  buttonLabel: event.target.value,
                }));
              }}
            />
          </div>
          <Divider />
          <Title level={5}>Aspetto</Title>
        <div className="landing-builder__inspector-row">
          <Text strong>Colore sfondo</Text>
          <Space align="center" style={{ width: '100%' }}>
            <ColorPicker
              value={formBlock.background ?? '#ffffff'}
              allowClear
              onChange={(_, hex) =>
                updateBlock(formBlock.id, (block) => ({
                  ...block,
                  background: hex ?? '',
                }))
              }
            />
            <Input
              value={formBlock.background ?? ''}
              placeholder="#ffffff"
              style={{ flex: 1, minWidth: 160 }}
              onChange={(event) =>
                updateBlock(formBlock.id, (block) => ({
                  ...block,
                  background: event.target.value,
                }))
              }
            />
          </Space>
        </div>
        <div className="landing-builder__inspector-row">
          <Text strong>Colore testo</Text>
          <Space align="center" style={{ width: '100%' }}>
            <ColorPicker
              value={formBlock.textColor ?? '#0f172a'}
              allowClear
              onChange={(_, hex) =>
                updateBlock(formBlock.id, (block) => ({
                  ...block,
                  textColor: hex ?? '',
                }))
              }
            />
            <Input
              value={formBlock.textColor ?? ''}
              placeholder="#0f172a"
              style={{ flex: 1, minWidth: 160 }}
              onChange={(event) =>
                updateBlock(formBlock.id, (block) => ({
                  ...block,
                  textColor: event.target.value,
                }))
              }
            />
          </Space>
        </div>
        <div className="landing-builder__inspector-row">
          <Text strong>Colore bottone</Text>
          <Space align="center" style={{ width: '100%' }}>
            <ColorPicker
              value={formBlock.buttonColor ?? draft.primaryColor ?? '#007AFF'}
              allowClear
              onChange={(_, hex) =>
                updateBlock(formBlock.id, (block) => ({
                  ...block,
                  buttonColor: hex ?? '',
                }))
              }
            />
            <Input
              value={formBlock.buttonColor ?? ''}
              placeholder="#007AFF"
              style={{ flex: 1, minWidth: 160 }}
              onChange={(event) =>
                updateBlock(formBlock.id, (block) => ({
                  ...block,
                  buttonColor: event.target.value,
                }))
              }
            />
          </Space>
        </div>

          <Divider />
          <Title level={5}>Campi</Title>
          <List
            dataSource={formBlock.fields}
            renderItem={(field, index) => {
              const isEmailField = field.type === 'email';
              return (
                <List.Item className="landing-builder__field-item" key={field.id}>
                  <div className="landing-builder__field-item-header">
                    <Text strong>{field.label}</Text>
                    <Space size="small">
                      <Tooltip title="Mostra campo">
                        <Switch
                          checked={isEmailField ? true : field.visible !== false}
                          disabled={isEmailField}
                          onChange={(checked) =>
                            updateBlock(formBlock.id, (block) => {
                              if (isEmailField) {
                                return block;
                              }
                              const nextFields = [...(block as LandingFormBlock).fields];
                              nextFields[index] = { ...nextFields[index], visible: checked };
                              return { ...(block as LandingFormBlock), fields: nextFields };
                            })
                          }
                        />
                      </Tooltip>
                      <Tooltip title="Campo obbligatorio">
                        <Switch
                          checked={isEmailField ? true : field.required ?? false}
                          disabled={isEmailField}
                          onChange={(checked) =>
                            updateBlock(formBlock.id, (block) => {
                              if (isEmailField && !checked) {
                                return block;
                              }
                              const nextFields = [...(block as LandingFormBlock).fields];
                              nextFields[index] = {
                                ...nextFields[index],
                                required: isEmailField ? true : checked,
                              };
                              return { ...(block as LandingFormBlock), fields: nextFields };
                            })
                          }
                        />
                      </Tooltip>
                    </Space>
                  </div>
                  <Input
                    value={field.placeholder ?? ''}
                    placeholder="Placeholder"
                    onChange={(event) =>
                      updateBlock(formBlock.id, (block) => {
                        const nextFields = [...(block as LandingFormBlock).fields];
                        nextFields[index] = {
                          ...nextFields[index],
                          placeholder: event.target.value,
                        };
                        return { ...(block as LandingFormBlock), fields: nextFields };
                      })
                    }
                  />
                </List.Item>
              );
            }}
          />

          <Text type="secondary" style={{ fontSize: 12 }}>
            Il consenso privacy viene richiesto automaticamente nel form pubblico.
          </Text>

          <Divider />
          <Space direction="vertical" style={{ width: '100%' }}>
            <Button
              icon={<CopyOutlined />}
              block
              onClick={() => duplicateBlock(formBlock.id)}
            >
              Duplica blocco
            </Button>
            <Button
              icon={<DeleteOutlined />}
              danger
              block
              onClick={() => removeBlock(formBlock.id)}
            >
              Elimina blocco
            </Button>
            <Button onClick={() => bumpZIndex(formBlock.id, 'up')} block>
              Porta avanti
            </Button>
            <Button onClick={() => bumpZIndex(formBlock.id, 'down')} block>
              Manda indietro
            </Button>
          </Space>
        </div>
      );
    }

    return null;
  };

  const effectiveOverlayOpacity = clamp01(
    draft.backgroundOverlayOpacity ?? draft.document.background?.overlayOpacity ?? 0,
  );
  const effectiveOverlayColor =
    draft.backgroundOverlayColor ?? draft.document.background?.overlayColor ?? '#0f172a';

  const canvasStyle = useMemo<React.CSSProperties>(() => {
    const baseColor = draft.backgroundColor ?? '#f3f4f6';
    const overlayOpacity = effectiveOverlayOpacity;
    const overlayColor = resolveOverlayColor(effectiveOverlayColor, overlayOpacity);

    if (draft.backgroundImageUrl) {
      const layers =
        overlayOpacity > 0
          ? [`linear-gradient(${overlayColor}, ${overlayColor})`, `url(${draft.backgroundImageUrl})`]
          : [`url(${draft.backgroundImageUrl})`];
      const backgroundSize = overlayOpacity > 0 ? 'cover, cover' : 'cover';
      const backgroundPosition = overlayOpacity > 0 ? 'center, center' : 'center';
      return {
        backgroundImage: layers.join(', '),
        backgroundSize,
        backgroundPosition,
        backgroundColor: baseColor,
      };
    }

    return {
      background: baseColor,
    };
  }, [draft.backgroundColor, draft.backgroundImageUrl, effectiveOverlayColor, effectiveOverlayOpacity]);

  const topbarSubtitle = draft.slug ? buildLandingPublicUrl(draft.slug) : 'slug non definito';

  if (loading) {
    return (
      <div className="landing-builder__loading">
        <Spin size="large" tip="Caricamento editor..." />
      </div>
    );
  }

  return (
    <div className="landing-builder">
      <header  className="landing-builder__topbar">
        <Space>
          <Tooltip title="Torna alla lista delle landing">
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/landing-pages')}>
               Indietro
            </Button>
          </Tooltip>
          <div className="landing-builder__topbar-info">
            <Title level={4} className="landing-builder__topbar-title">
              {draft.name || 'Nuova landing'}
            </Title>
            <Text type="secondary">{topbarSubtitle}</Text>
            <Tag
              color={draft.customerName ? 'blue' : 'gold'}
              style={{ alignSelf: 'flex-start', marginTop: 2 }}
            >
              {draft.customerName || 'Cliente non selezionato'}
            </Tag>
          </div>
        </Space>

        <Space wrap>
          <Dropdown menu={{ items: blockMenuItems, onClick: onBlockMenuClick }} placement="bottomRight">
            <Button icon={<PlusOutlined />}>Aggiungi blocco</Button>
          </Dropdown>
          <Button icon={<SettingOutlined />} onClick={() => setLandingSettingsOpen(true)}>
            Impostazioni landing
          </Button>
          <Button icon={<CalendarOutlined />} onClick={() => setEmailSettingsOpen(true)}>
            Impostazioni email/evento
          </Button>
          <Button icon={<SaveOutlined />} loading={saving} onClick={() => handleSave(false)}>
            Salva bozza
          </Button>
          <Button type="primary" loading={saving} onClick={() => handleSave(true)}>
            Pubblica
          </Button>
        </Space>
      </header>

      <div className="landing-builder__content">
        <aside
          className={[
            'landing-builder__sidebar',
            sidebarCollapsed ? 'landing-builder__sidebar--collapsed' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div className="landing-builder__panel-header">
            <Title level={5} className="landing-builder__panel-title">
              Blocchi
            </Title>
            <Tooltip
              placement="right"
              title={sidebarCollapsed ? 'Apri pannello blocchi' : 'Chiudi pannello blocchi'}
            >
              <Button
                type="text"
                size="small"
                icon={sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setSidebarCollapsed((prev) => !prev)}
              />
            </Tooltip>
          </div>
          <div className="landing-builder__sidebar-content">
            <List
              dataSource={blocks}
              size="small"
              locale={{ emptyText: <Empty description="Canvas vuoto" /> }}
              renderItem={(block) => {
                const isSelected = block.id === selectedBlockId;
                return (
                  <List.Item
                    className={[
                      'landing-builder__sidebar-item',
                      isSelected ? 'landing-builder__sidebar-item--active' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => selectBlock(block.id)}
                  >
                    <div className="landing-builder__sidebar-item-title">
                      <Text strong>{BLOCK_LABEL[block.type as BlockType]}</Text>
                      <Text type="secondary" ellipsis style={{ display: 'block', maxWidth: 160 }}>
                        {blockPreviewText(block)}
                      </Text>
                    </div>
                    <Button
                      icon={<DeleteOutlined />}
                      size="small"
                      type="text"
                      onClick={(event) => {
                        event.stopPropagation();
                        removeBlock(block.id);
                      }}
                    />
                  </List.Item>
                );
              }}
            />
            <Divider />
            <Dropdown menu={{ items: blockMenuItems, onClick: onBlockMenuClick }} placement="bottomLeft">
              <Button icon={<PlusOutlined />} block>
                Aggiungi blocco
              </Button>
            </Dropdown>
           </div>
        </aside>

        <main className="landing-builder__canvas-area">
          <div className="landing-builder__canvas-shell">
            <div
              className="landing-builder__canvas"
              style={{
                ...canvasStyle,
                width: canvasWidth,
                height: canvasHeight,
              }}
              ref={viewportRef}
              onClick={() => {
                selectBlock(null);
                closeContextMenu();
              }}
              onContextMenu={handleContextMenu}
            >
              <div
              className="landing-builder__canvas-safe"
              style={{ minHeight: canvasHeight }}
            >
                {guides.vertical != null && (
                  <div
                    className="landing-builder__guide landing-builder__guide--vertical"
                    style={{ left: guides.vertical }}
                  />
                )}
                {guides.horizontal != null && (
                  <div
                    className="landing-builder__guide landing-builder__guide--horizontal"
                    style={{ top: guides.horizontal }}
                  />
                )}
                {blocks.map(renderBlock)}
                {renderFooterPreview()}
                {contextMenu ? (
                  <div
                    className="landing-builder__context-menu"
                    style={{ left: contextMenu.x, top: contextMenu.y }}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Menu
                      items={contextMenu.blockId ? blockActionMenuItems : blockMenuItems}
                      selectable={false}
                      onClick={({ key }) => {
                        if (contextMenu.blockId) {
                          if (key === 'duplicate') {
                            duplicateBlock(contextMenu.blockId);
                          } else if (key === 'forward') {
                            bumpZIndex(contextMenu.blockId, 'up');
                          } else if (key === 'backward') {
                            bumpZIndex(contextMenu.blockId, 'down');
                          } else if (key === 'delete') {
                            removeBlock(contextMenu.blockId);
                          }
                        } else {
                          addBlock(key as BlockType, { x: contextMenu.x, y: contextMenu.y });
                        }
                        closeContextMenu();
                      }}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </main>

        <aside
          className={[
            'landing-builder__inspector',
            inspectorCollapsed ? 'landing-builder__inspector--collapsed' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div className="landing-builder__panel-header">
            <Title level={5} className="landing-builder__panel-title">
              Inspector
            </Title>
            <Tooltip
              placement="left"
              title={inspectorCollapsed ? 'Apri inspector' : 'Chiudi inspector'}
            >
              <Button
                type="text"
                size="small"
                icon={inspectorCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setInspectorCollapsed((prev) => !prev)}
              />
            </Tooltip>
          </div>
          <div className="landing-builder__inspector-content-wrapper">
            {renderInspector()}
          </div>
        </aside>
      </div>

      <Drawer
        title="Impostazioni landing"
        placement="right"
        width={420}
        open={landingSettingsOpen}
        onClose={() => setLandingSettingsOpen(false)}
        destroyOnClose
      >
        <Form layout="vertical">
          <Form.Item label="Nome landing">
            <Input
              value={draft.name}
              placeholder="Nome interno"
              onChange={(event) => handlePageFieldChange('name', event.target.value)}
            />
          </Form.Item>
          <Form.Item
            label="Slug (URL)"
            extra={`Solo lettere, numeri e trattini. Verr\u00E0 usato per raggiungere la landing pubblica su ${LANDING_PUBLIC_BASE_URL}.`}
            validateStatus={slugValidateStatus}
            help={slugHelpMessage}
          >
            <Input
              value={draft.slug}
              placeholder="es. promo-gin"
              prefix={`${LANDING_PUBLIC_BASE_URL}/`}
              suffix={slugSuffix}
              maxLength={160}
              onChange={(event) => {
                const sanitized = event.target.value
                  .toLowerCase()
                  .replace(/\s+/g, '-')
                  .replace(/[^a-z0-9-]/g, '-')
                  .replace(/-+/g, '-')
                  .replace(/^-+|-+$/g, '');
                handlePageFieldChange('slug', sanitized);
                if (slugCheckTimerRef.current !== null) {
                  window.clearTimeout(slugCheckTimerRef.current);
                  slugCheckTimerRef.current = null;
                }
                if (sanitized.trim() === '') {
                  lastSlugKeyRef.current = '';
                  setSlugCheck({ status: 'idle' });
                } else {
                  lastSlugKeyRef.current = '';
                }
              }}
            />
          </Form.Item>

          <Form.Item label="Cliente associato" required>
            <Select<number>
              showSearch
              placeholder="Seleziona cliente"
              optionFilterProp="label"
              value={draft.customerId ?? undefined}
              loading={customersLoading}
              style={{ width: '100%' }}
              onDropdownVisibleChange={(open) => {
                if (open && customerOptions.length === 0 && !customersLoading) {
                  void loadCustomers();
                }
              }}
              onChange={(value) => {
                const match = customerOptions.find((customer) => customer.id === value);
                setDraft((prev) => ({
                  ...prev,
                  customerId: value,
                  customerName: match?.businessName ?? prev.customerName,
                }));
              }}
              filterOption={(input, option) =>
                (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
              }
              options={customerOptions.map((customer) => ({
                value: customer.id,
                label: customer.businessName ?? `Cliente #${customer.id}`,
              }))}
            />
          </Form.Item>

          <Form.Item label="Font predefinito">
            <Select
              options={FONT_OPTIONS}
              value={draft.document.metadata?.defaultFontFamily ?? 'inherit'}
              onChange={(value) =>
                setDraft((prev) => ({
                  ...prev,
                  document: {
                    ...prev.document,
                    metadata: {
                      ...(prev.document.metadata ?? {}),
                      defaultFontFamily: value,
                    },
                    blocks: prev.document.blocks.map((block) => {
                      if (
                        isFontBlock(block) &&
                        (block.fontFamily === 'inherit' ||
                          block.fontFamily ===
                            (prev.document.metadata?.defaultFontFamily ?? 'inherit'))
                      ) {
                        return {
                          ...block,
                          fontFamily: value,
                        };
                      }
                      return block;
                    }),
                  },
                }))
              }
            />
          </Form.Item>
          <Form.Item label="Altezza pagina (px)">
            <InputNumber
              min={600}
              max={2000}
              value={draft.document.size?.height ?? 932}
              style={{ width: '100%' }}
              onChange={(value) =>
                setDraft((prev) => ({
                  ...prev,
                  document: {
                    ...prev.document,
                    size: {
                      width: prev.document.size?.width ?? 430,
                      height: value ?? 932,
                    },
                  },
                }))
              }
            />
          </Form.Item>

          <Form.Item label="Descrizione breve">
            <TextArea
              value={draft.description}
              placeholder="Descrizione per uso interno"
              autoSize={{ minRows: 2, maxRows: 4 }}
              onChange={(event) => handlePageFieldChange('description', event.target.value)}
            />
          </Form.Item>
          <Divider />
          <Title level={5}>Branding</Title>

          <Form.Item label="Colore di sfondo">
            <Space align="center" style={{ width: '100%' }}>
              <ColorPicker
                value={draft.backgroundColor ?? '#FFFFFF'}
                allowClear
                onChange={(_, hex) => handlePageFieldChange('backgroundColor', hex ?? '')}
              />
              <Input
                value={draft.backgroundColor ?? ''}
                placeholder="#FFFFFF"
                style={{ flex: 1, minWidth: 160 }}
                onChange={(event) => handlePageFieldChange('backgroundColor', event.target.value)}
              />
            </Space>
          </Form.Item>

          <Form.Item label="Immagine di sfondo">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Input
                value={draft.backgroundImageUrl ?? ''}
                placeholder="https://..."
                onChange={(event) => handlePageFieldChange('backgroundImageUrl', event.target.value)}
              />
              <Space wrap>
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  customRequest={handleBackgroundUpload}
                >
                  <Button icon={<UploadOutlined />} loading={backgroundUploading}>
                    Carica sfondo
                  </Button>
                </Upload>
                <Button icon={<SearchOutlined />} onClick={openImageSearchForBackground}>
                  Cerca sul web
                </Button>
                {draft.backgroundImageUrl ? (
                  <Button onClick={() => handlePageFieldChange('backgroundImageUrl', '')}>
                    Rimuovi sfondo
                  </Button>
                ) : null}
              </Space>
              {draft.backgroundImageUrl ? (
                <div className="landing-builder__image-preview">
                  <Image
                    src={draft.backgroundImageUrl}
                    alt="Anteprima sfondo"
                    height={160}
                    width="100%"
                    style={{ objectFit: 'cover' }}
                  />
                </div>
              ) : (
                <Text type="secondary">Nessuna immagine selezionata</Text>
              )}
            </Space>
          </Form.Item>

          <Form.Item label="Colore overlay sfondo">
            <Space align="center" style={{ width: '100%' }}>
              <ColorPicker
                value={draft.backgroundOverlayColor ?? effectiveOverlayColor}
                allowClear
                onChange={(_, hex) => handlePageFieldChange('backgroundOverlayColor', hex ?? '')}
              />
              <Input
                value={draft.backgroundOverlayColor ?? ''}
                placeholder="#0F172A"
                style={{ flex: 1, minWidth: 160 }}
                onChange={(event) => handlePageFieldChange('backgroundOverlayColor', event.target.value)}
              />
            </Space>
          </Form.Item>

          <Form.Item label="Opacità overlay">
            <Slider
              min={0}
              max={100}
              value={Math.round(effectiveOverlayOpacity * 100)}
              onChange={(value) =>
                handlePageFieldChange('backgroundOverlayOpacity', (value as number) / 100)
              }
              tooltip={{ formatter: (value) => `${value}%` }}
            />
          </Form.Item>

          <Form.Item label="Colore primario">
            <Space align="center" style={{ width: '100%' }}>
              <ColorPicker
                value={draft.primaryColor ?? '#007AFF'}
                allowClear
                onChange={(_, hex) => handlePageFieldChange('primaryColor', hex ?? '')}
              />
              <Input
                value={draft.primaryColor ?? ''}
                placeholder="#007AFF"
                style={{ flex: 1, minWidth: 160 }}
                onChange={(event) => handlePageFieldChange('primaryColor', event.target.value)}
              />
            </Space>
          </Form.Item>
        </Form>
      </Drawer>

      <Drawer
        title="Impostazioni email/evento"
        placement="right"
        width={420}
        open={emailSettingsOpen}
        onClose={() => setEmailSettingsOpen(false)}
        destroyOnClose
      >
        <Form layout="vertical">
          <Title level={5}>Email coupon</Title>

          <Form.Item label="Oggetto email coupon">
            <Input
              value={draft.couponEmailSubject ?? ''}
              placeholder="Es. Ecco il tuo coupon esclusivo"
              onChange={(event) => handlePageFieldChange('couponEmailSubject', event.target.value)}
            />
          </Form.Item>

          <Form.Item
            label="Testo email coupon"
            extra="Invia il testo completo della mail, puoi usare i placeholder {{nome}}, {{cognome}}, {{landing}} e {{data_evento}}. Il QR code viene aggiunto automaticamente."
          >
            <TextArea
              value={draft.couponEmailBody ?? ''}
              placeholder="Ciao {{nome}}, grazie per aver partecipato. Il tuo coupon: ..."
              autoSize={{ minRows: 3, maxRows: 6 }}
              onChange={(event) => handlePageFieldChange('couponEmailBody', event.target.value)}
            />
          </Form.Item>

          <Divider />
          <Title level={5}>Evento & Reminder</Title>

          <Form.Item label="Data evento" extra="Serve per pianificare l'invio delle comunicazioni e mostrare info sul calendario.">
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="DD/MM/YYYY HH:mm"
              value={eventDateValue ?? null}
              style={{ width: '100%' }}
              allowClear
              disabledDate={disablePastDates}
              onChange={(value) =>
                handlePageFieldChange('eventDate', value && value.isValid() ? value.toISOString() : null)
              }
            />
          </Form.Item>

          <Form.Item colon={false} label={null}>
            <div className="landing-builder__drawer-switch">
              <div className="landing-builder__drawer-switch-text">
                <Text strong>Reminder automatico</Text>
                <Text type="secondary">Attiva l'invio di email di promemoria e follow-up.</Text>
              </div>
              <Switch checked={reminderEnabled} onChange={handleReminderToggle} />
            </div>
          </Form.Item>

          <Form.Item
            label="Promemoria (data/ora)"
            extra="Se impostato, utilizzeremo questa data per inviare il reminder automatico."
          >
            <DatePicker
              showTime={{ format: 'HH:mm' }}
              format="DD/MM/YYYY HH:mm"
              value={reminderDateValue ?? null}
              style={{ width: '100%' }}
              allowClear
              disabledDate={reminderDisabledDate}
              disabled={!reminderEnabled}
              onChange={(value) =>
                handlePageFieldChange('reminderDate', value && value.isValid() ? value.toISOString() : null)
              }
            />
          </Form.Item>

          <Form.Item label="Oggetto email reminder">
            <Input
              value={draft.reminderEmailSubject ?? ''}
              placeholder="Es. Promemoria evento special promo"
              disabled={!reminderEnabled}
              onChange={(event) => handlePageFieldChange('reminderEmailSubject', event.target.value)}
            />
          </Form.Item>

          <Form.Item
            label="Testo email reminder"
            extra="Puoi usare testo semplice e i placeholder {{nome}}, {{cognome}}, {{coupon}} e {{data_evento}}."
          >
            <TextArea
              value={draft.reminderEmailBody ?? ''}
              placeholder="Ciao {{nome}}, ti ricordiamo che l'evento inizierà il..."
              autoSize={{ minRows: 3, maxRows: 6 }}
              disabled={!reminderEnabled}
              onChange={(event) => handlePageFieldChange('reminderEmailBody', event.target.value)}
            />
          </Form.Item>
        </Form>
      </Drawer>

      <Modal
        title="Cerca immagini gratuite"
        open={imageSearchOpen}
        onCancel={() => setImageSearchOpen(false)}
        footer={null}
        width={760}
        destroyOnClose
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Search
            placeholder="Cerca immagini (es. tecnologia, spiaggia, ufficio...)"
            allowClear
            enterButton="Cerca"
            value={imageSearchQuery}
            onChange={(event) => {
              const value = event.target.value;
              setImageSearchQuery(value);
              if (!value.trim()) {
                setImageSearchResults([]);
              }
            }}
            onSearch={(value) => {
              void performImageSearch(value);
            }}
            loading={imageSearchLoading}
          />
          <div className="landing-builder__image-search-results">
            {imageSearchLoading ? (
              <div className="landing-builder__image-search-loading">
                <Spin />
              </div>
            ) : imageSearchResults.length === 0 ? (
              <Empty
                description={
                  imageSearchQuery
                    ? 'Nessun risultato trovato'
                    : 'Inserisci un termine di ricerca'
                }
              />
            ) : (
              imageSearchResults.map((result) => (
                <button
                  key={result.id}
                  type="button"
                  className="landing-builder__image-search-card"
                  onClick={() => handleApplyImageResult(result)}
                >
                  <img src={result.thumbnail} alt={result.title || 'Anteprima'} />
                  <div className="landing-builder__image-search-card-body">
                    <span className="landing-builder__image-search-card-title">
                      {result.title || 'Senza titolo'}
                    </span>
                    {result.provider ? (
                      <span className="landing-builder__image-search-card-provider">
                        {result.provider}
                      </span>
                    ) : null}
                  </div>
                </button>
              ))
            )}
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default LandingBuilderPage;







